import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { SelectionOption } from '../select/select.component';

/**
 * Maps the canonical validity controls onto the actual control names used by the
 * parent form, so consumers (discount, subscription) can keep their own field names.
 */
export interface ValidityPeriodControlNames {
  limit: string;
  periodType: string;
  quantity: string;
  start: string;
  finish: string;
}

const DEFAULT_CONTROL_NAMES: ValidityPeriodControlNames = {
  limit: 'validity_period_limit',
  periodType: 'validity_period_type',
  quantity: 'validity_period_quantity',
  start: 'validity_period_start',
  finish: 'validity_period_finish',
};

/**
 * Shared validity-period editor used by the discount wizard and the subscription
 * product wizard. It renders the enable toggle, period-type selector, duration
 * quantity input and the fixed-dates start/finish pickers, and owns the
 * conditional validation + end-of-day handling.
 *
 * The parent owns the FormGroup (so it maps to/from its own API shape); this
 * component only reads/validates the controls named by `controls`.
 */
@Component({
  selector: 'lib-validity-period',
  templateUrl: './validity-period.component.html',
  styleUrls: ['./validity-period.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ValidityPeriodComponent implements OnInit, OnChanges, OnDestroy {
  /** Parent-owned form group holding the validity controls. */
  @Input() form!: FormGroup;
  /** Names of the validity controls within `form`. */
  @Input() controls: ValidityPeriodControlNames = DEFAULT_CONTROL_NAMES;
  /** Date-only or date + time pickers for the fixed-dates range. */
  @Input() mode: 'date' | 'datetime' = 'datetime';
  /** Options for the period-type selector (keys are translated by the parent's namespace). */
  @Input() periodTypeOptions: SelectionOption[] = [];
  /** Enum value that represents "fixed dates" (Years/Months/Days otherwise). */
  @Input() fixedDatesValue: number = 3;

  // Already-translated label/info/error strings supplied by the parent.
  @Input() limitLabel = '';
  @Input() limitInfo?: string;
  @Input() periodTypeLabel = '';
  @Input() quantityLabel = '';
  @Input() startLabel = '';
  @Input() startInfo?: string;
  @Input() finishLabel = '';
  @Input() finishInfo?: string;
  @Input() requiredError = '';

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.limitCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyValidators());
    this.periodTypeCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyValidators());

    if (this.mode === 'datetime') {
      this.finishCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: Date | string | null) => {
        if (!value) return;
        const date = new Date(value);
        if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
          setTimeout(() => this.finishCtrl?.patchValue(this.toEndOfDay(value), { emitEvent: false }), 0);
        }
      });
    }

    this.applyValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-apply when the parent swaps in a new form (e.g. edit vs create).
    if (changes['form'] && !changes['form'].firstChange) {
      this.applyValidators();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get limitCtrl(): AbstractControl | null {
    return this.form?.get(this.controls.limit) ?? null;
  }

  get periodTypeCtrl(): AbstractControl | null {
    return this.form?.get(this.controls.periodType) ?? null;
  }

  get quantityCtrl(): AbstractControl | null {
    return this.form?.get(this.controls.quantity) ?? null;
  }

  get startCtrl(): AbstractControl | null {
    return this.form?.get(this.controls.start) ?? null;
  }

  get finishCtrl(): AbstractControl | null {
    return this.form?.get(this.controls.finish) ?? null;
  }

  // lib-select is not a form directive, so a plain setValue would leave the control pristine and the
  // wizards' unsaved-changes prompts would never fire for a period-type change.
  onPeriodTypeChange(value: unknown): void {
    this.periodTypeCtrl?.setValue(value);
    this.periodTypeCtrl?.markAsDirty();
  }

  get isLimited(): boolean {
    return !!this.limitCtrl?.value;
  }

  get isFixedDates(): boolean {
    return this.periodTypeCtrl?.value === this.fixedDatesValue;
  }

  get isDuration(): boolean {
    const value = this.periodTypeCtrl?.value;
    return value != null && value !== this.fixedDatesValue;
  }

  private applyValidators(): void {
    const limited = this.isLimited;
    const fixedDates = this.isFixedDates;

    this.startCtrl?.setValidators(limited && fixedDates ? [Validators.required] : null);
    this.finishCtrl?.setValidators(limited && fixedDates ? [Validators.required] : null);
    this.quantityCtrl?.setValidators(limited && !fixedDates ? [Validators.required, Validators.min(1)] : null);

    this.startCtrl?.updateValueAndValidity({ emitEvent: false });
    this.finishCtrl?.updateValueAndValidity({ emitEvent: false });
    this.quantityCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  private toEndOfDay(value: Date | string | null | undefined): Date | null {
    if (!value) return null;
    const date = new Date(value);
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(59);
    return date;
  }
}
